/**
 * Projektliste mit dem, was vor der Abfahrt zaehlt: Ist das Projekt reisefertig
 * (PV-B02)? Mehrere auf einmal vorbereiten geht auch (PV-B03).
 */

import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db, neueId, speichere } from '../daten/db.ts';
import { kachelliste, pruefeKacheln } from '../daten/kacheln.ts';
import { alsSatz, bereiteProjektVor } from '../daten/vorbereiten.ts';
import type { Kartenquelle } from './Karte.tsx';
import type { Reiter } from './Projektansicht.tsx';
import type { Projekt } from '../typen.ts';
import { faelligText, projektlage, schrittstaende, type Projektlage } from '../abwicklung.ts';
import { loeseAuf, VORGABEN } from '../einstellungen.ts';

interface Eigenschaften {
  quelle: Kartenquelle;
  vorladen: { umkreisM: number; minZoom: number; maxZoom: number };
  onOeffnen: (id: string, reiter?: Reiter) => void;
}

const kurz = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-AT', { day: 'numeric', month: 'numeric' }) : '';

export function Projektliste({ quelle, vorladen, onOeffnen }: Eigenschaften): React.JSX.Element {
  const projekte = useLiveQuery(
    () => db.projekte.filter((p) => !p.geloescht_am).toArray(),
    [],
    [] as Projekt[],
  );
  // Entfernt heisst hier weich entfernt. Angezeigt wird es trotzdem — sonst ist
  // ein Fehlgriff von einem nie angelegten Projekt nicht zu unterscheiden.
  const entfernte = useLiveQuery(
    () => db.projekte.filter((p) => !!p.geloescht_am).toArray(),
    [],
    [] as Projekt[],
  );
  const kachelstaende = useLiveQuery(() => db.kachelstand.toArray(), [], []);

  // Wo steht jedes Projekt nach dem Angebot (Mathias, 11/2026)? Gerechnet aus
  // Angeboten, Aufträgen, Schritten und Dokumenten — wie in der Abwicklung.
  const angebote = useLiveQuery(() => db.angebote.toArray(), [], []);
  const auftraege = useLiveQuery(() => db.auftraege.toArray(), [], []);
  const schritte = useLiveQuery(() => db.projektschritte.toArray(), [], []);
  const dokumente = useLiveQuery(() => db.dokumente.toArray(), [], []);
  const globaleEinstellungen = useLiveQuery(() => db.einstellungen.toArray(), [], []);
  const projekteinstellungen = useLiveQuery(() => db.projekteinstellungen.toArray(), [], []);
  const heute = new Date();
  const lagen = new Map<string, Projektlage>(
    projekte.map((p) => [
      p.id,
      projektlage(
        schrittstaende({
          angebote: angebote.filter((a) => a.projekt_id === p.id),
          auftraege: auftraege.filter((a) => a.projekt_id === p.id),
          schritte: schritte.filter((x) => x.projekt_id === p.id),
          dokumente: dokumente.filter((d) => d.projekt_id === p.id && !d.geloescht_am),
          vorgabe: loeseAuf(
            { global: globaleEinstellungen, jeProjekt: projekteinstellungen.filter((e) => e.projekt_id === p.id) },
            'abwicklung.erinnerung',
            VORGABEN['abwicklung.erinnerung'],
          ).wert,
          heute,
        }),
      ),
    ]),
  );
  const erinnerungen = projekte
    .flatMap((p) => (lagen.get(p.id)?.faellig ?? []).map((s) => ({ projekt: p, schritt: s })))
    .sort((a, b) => (b.schritt.tageUeber ?? 0) - (a.schritt.tageUeber ?? 0));
  const [laedt, setLaedt] = useState<string | null>(null);
  const [fortschritt, setFortschritt] = useState<{ geladen: number; gesamt: number } | null>(null);
  const [umrisse, setUmrisse] = useState<string | null>(null);
  const [pruefe, setPruefe] = useState(false);

  async function legeAn(): Promise<void> {
    const projekt: Projekt = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      nummer: null,
      kunde_name: 'Neues Projekt',
      kunde_email: null,
      kunde_telefon: null,
      strasse: null,
      plz: null,
      ort: null,
      lat: null,
      lng: null,
      zaehlpunktnummer: null,
      status: 'anfrage',
      notizen: null,
    };
    await speichere('projekte', projekt, {
      kunde_name: projekt.kunde_name,
      status: projekt.status,
    });
    onOeffnen(projekt.id);
  }

  async function bereiteVor(projekt: Projekt): Promise<void> {
    if (projekt.lat === null || projekt.lng === null) return;
    setLaedt(projekt.id);
    setUmrisse(null);
    // Ohne `finally` blieben nach einem Fehler alle „Vorbereiten“-Knöpfe
    // gesperrt, bis die Seite neu geladen wurde.
    try {
      const bericht = await bereiteProjektVor(
        { id: projekt.id, lat: projekt.lat, lng: projekt.lng },
        quelle,
        vorladen,
        setFortschritt,
      );
      setUmrisse(alsSatz(bericht));
    } finally {
      setLaedt(null);
      setFortschritt(null);
    }
  }

  async function nimmWiederAuf(projekt: Projekt): Promise<void> {
    await speichere('projekte', projekt, { geloescht_am: null });
  }

  /**
   * Nachsehen, was wirklich auf dem Gerät liegt (PV-B02).
   *
   * Der Knopf hat immer schon gearbeitet — nur schweigend. Wer ihn drückte,
   * sah nichts geschehen und hielt ihn für wirkungslos; besonders dann, wenn
   * alle Projekte ohne Adresse waren und es tatsächlich nichts zu prüfen gab.
   * Jetzt steht hinterher da, was geprüft wurde und was nicht.
   */
  async function pruefeAlle(): Promise<void> {
    setPruefe(true);
    setUmrisse(null);
    const geprueft: string[] = [];
    const ohneAdresse: string[] = [];
    try {
      for (const projekt of projekte) {
        const name = projekt.kunde_name || 'Ohne Namen';
        if (projekt.lat === null || projekt.lng === null) {
          ohneAdresse.push(name);
          continue;
        }
        const kacheln = kachelliste(
          projekt.lat,
          projekt.lng,
          vorladen.umkreisM,
          vorladen.minZoom,
          Math.min(vorladen.maxZoom, quelle.maxZoom),
        );
        const stand = await pruefeKacheln(quelle.url, kacheln);
        await db.kachelstand.put({
          projekt_id: projekt.id,
          geladen: stand.geladen,
          gesamt: stand.gesamt,
          stand: new Date().toISOString(),
        });
        geprueft.push(`${name}: ${stand.geladen} von ${stand.gesamt} Kacheln`);
      }

      const teile: string[] = [];
      if (geprueft.length > 0) teile.push(geprueft.join(' · '));
      if (ohneAdresse.length > 0) {
        teile.push(
          `Ohne Adresse und darum nicht prüfbar: ${ohneAdresse.join(', ')}. Erst den Standort setzen, dann vorbereiten.`,
        );
      }
      setUmrisse(
        teile.length > 0 ? teile.join(' — ') : 'Noch kein Projekt da, das sich prüfen ließe.',
      );
    } finally {
      setPruefe(false);
    }
  }

  return (
    <div className="liste">
      <p className="marke">Projekte</p>
      <h1>Aufnahme und Planung</h1>

      <div className="knopfreihe">
        <button className="wichtig" onClick={() => void legeAn()}>
          Projekt anlegen
        </button>
        <button onClick={() => void pruefeAlle()} disabled={pruefe}>
          {pruefe ? 'Sieht nach …' : 'Reisefertigkeit prüfen'}
        </button>
      </div>

      {laedt && fortschritt && (
        <div className="hinweis">
          Kartenkacheln werden geladen: {fortschritt.geladen} von {fortschritt.gesamt}
        </div>
      )}
      {!laedt && umrisse && <div className="hinweis">{umrisse}</div>}

      {erinnerungen.length > 0 && (
        <div className="kachel erinnerungen">
          <h2 style={{ marginTop: 0 }}>Fällig ({erinnerungen.length})</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {erinnerungen.map(({ projekt, schritt }) => (
              <li key={`${projekt.id}-${schritt.schritt}`}>
                <span>
                  <strong>{projekt.kunde_name || 'Ohne Namen'}</strong> — {schritt.titel}
                  <span className="klein gedaempft"> · {faelligText(schritt, heute)}</span>
                </span>
                <button onClick={() => onOeffnen(projekt.id, 'abwicklung')}>Öffnen</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {projekte.length === 0 && (
        <p className="gedaempft">
          Noch keine Projekte. Ein neues Projekt braucht Kunde, Adresse und Zählpunktnummer — alles
          Weitere entsteht auf der Karte.
        </p>
      )}

      {projekte.map((projekt) => {
        const kachel = kachelstaende.find((k) => k.projekt_id === projekt.id);
        return (
          <div key={projekt.id} className="projektzeile">
            <button
              onClick={() => onOeffnen(projekt.id)}
              className="schlicht"
              style={{ padding: 0, textAlign: 'left', color: 'inherit' }}
            >
              <div className="titel">{projekt.kunde_name || 'Ohne Namen'}</div>
              <div className="ort">
                {[projekt.strasse, projekt.plz, projekt.ort].filter(Boolean).join(', ') ||
                  'Adresse fehlt'}
                {projekt.schmutzig ? ' · nicht abgeglichen' : ''}
              </div>
              {(() => {
                const lage = lagen.get(projekt.id);
                if (!lage) return null;
                const a = lage.angebotVersendet;
                const b = lage.auftragErhalten;
                const n = lage.naechster;
                return (
                  <div className="projektlage">
                    <span className={`lagemarke ${a.zustand === 'erledigt' ? 'lagemarke--ja' : ''}`}>
                      {a.zustand === 'erledigt'
                        ? `Angebot versendet ${kurz(a.erledigtAm)}`
                        : a.zustand === 'entfaellt'
                          ? 'Ohne Angebot'
                          : 'Kein Angebot versendet'}
                    </span>
                    <span className={`lagemarke ${b.zustand === 'erledigt' ? 'lagemarke--ja' : ''}`}>
                      {b.zustand === 'erledigt' ? `Auftrag ${kurz(b.erledigtAm)}` : 'Kein Auftrag'}
                    </span>
                    {n && (
                      <span className={`lagemarke ${n.zustand === 'faellig' ? 'lagemarke--faellig' : ''}`}>
                        Als Nächstes: {n.titel}
                        {n.zustand === 'faellig' ? ` · ${faelligText(n, heute)}` : ''}
                      </span>
                    )}
                    {!n && lage.erledigt === lage.gesamt && <span className="lagemarke lagemarke--ja">Abgewickelt</span>}
                    {lage.zuletzt && (
                      <span className="lagemarke">
                        zuletzt: {lage.zuletzt.titel} {kurz(lage.zuletzt.erledigtAm)}
                        {lage.zuletzt.erledigtVon ? ` · ${lage.zuletzt.erledigtVon}` : ''}
                      </span>
                    )}
                  </div>
                );
              })()}
            </button>

            <span className={`reisefertig ${reisefertigKlasse(kachel)}`}>
              {reisefertigText(kachel)}
            </span>

            <button
              onClick={() => void bereiteVor(projekt)}
              disabled={projekt.lat === null || laedt !== null}
              title={
                projekt.lat === null
                  ? 'Erst die Adresse auf der Karte setzen'
                  : 'Kartenkacheln für den Umkreis laden'
              }
            >
              Vorbereiten
            </button>
          </div>
        );
      })}

      {entfernte.length > 0 && (
        <>
          <h2 style={{ marginTop: '2rem' }}>Entfernt ({entfernte.length})</h2>
          <p className="klein" style={{ margin: '0 0 0.6rem' }}>
            Entfernte Projekte bleiben stehen, damit Angebotsnummern und Aufträge nicht ins Leere
            zeigen. Wieder aufgenommen sind sie vollständig da — mit Flächen, Fotos und Angebot.
          </p>
          {entfernte.map((projekt) => (
            <div key={projekt.id} className="projektzeile" style={{ opacity: 0.7 }}>
              <div>
                <div className="titel">{projekt.kunde_name || 'Ohne Namen'}</div>
                <div className="ort">
                  {[projekt.strasse, projekt.plz, projekt.ort].filter(Boolean).join(', ') ||
                    'Adresse fehlt'}
                  {projekt.geloescht_am
                    ? ` · entfernt am ${new Date(projekt.geloescht_am).toLocaleString('de-AT')}`
                    : ''}
                </div>
              </div>
              <span />
              <button onClick={() => void nimmWiederAuf(projekt)}>Wieder aufnehmen</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function anteil(kachel: { geladen: number; gesamt: number } | undefined): number {
  if (!kachel || kachel.gesamt === 0) return 0;
  return kachel.geladen / kachel.gesamt;
}

function reisefertigText(kachel: { geladen: number; gesamt: number } | undefined): string {
  const wert = anteil(kachel);
  if (wert >= 0.98) return 'Karte vollständig';
  if (wert > 0) return `Karte ${Math.round(wert * 100)} %`;
  return 'Karte fehlt';
}

function reisefertigKlasse(kachel: { geladen: number; gesamt: number } | undefined): string {
  const wert = anteil(kachel);
  if (wert >= 0.98) return 'reisefertig--ja';
  if (wert > 0) return 'reisefertig--teil';
  return 'reisefertig--nein';
}
